import React from "react";
import {
  Image,
  ImageSourcePropType,
  StyleSheet,
  Text,
  TouchableOpacity,
  ViewStyle,
} from "react-native";
import colors from "@constants/color";

interface CheckboxProps {
  // checked state — accept both naming conventions
  value?: boolean;
  checked?: boolean;
  // change handlers — accept both conventions
  onValueChange?: (value: boolean) => void;
  onChange?: () => void;
  // box-mode colours
  tintColors?: { true?: string; false?: string };
  style?: ViewStyle;
  // image-mode (renders an image instead of the drawn box)
  checkedImage?: ImageSourcePropType;
  uncheckedImage?: ImageSourcePropType;
  imageStyle?: ViewStyle;
  // optional label rendered inline (image-mode only)
  label?: string;
  containerStyle?: ViewStyle;
}

const Checkbox: React.FC<CheckboxProps> = ({
  value,
  checked,
  onValueChange,
  onChange,
  tintColors,
  style,
  checkedImage,
  uncheckedImage,
  imageStyle,
  label,
  containerStyle,
}) => {
  const isChecked = value ?? checked ?? false;

  const handlePress = () => {
    onValueChange?.(!isChecked);
    onChange?.();
  };

  if (checkedImage && uncheckedImage) {
    return (
      <TouchableOpacity
        onPress={handlePress}
        style={[styles.imageRow, containerStyle]}
        activeOpacity={0.7}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <Image
          source={isChecked ? checkedImage : uncheckedImage}
          style={[styles.image, imageStyle]}
          resizeMode="contain"
        />
        {!!label && <Text style={styles.imageLabel}>{label}</Text>}
      </TouchableOpacity>
    );
  }

  const checkedColor = tintColors?.true ?? colors.plain_green;
  const uncheckedBorderColor = tintColors?.false ?? colors.lightMediumBlack;

  return (
    <TouchableOpacity
      onPress={handlePress}
      style={[
        styles.box,
        isChecked
          ? { backgroundColor: checkedColor, borderColor: checkedColor }
          : {
              backgroundColor: colors.white,
              borderColor: uncheckedBorderColor,
            },
        style,
      ]}
      activeOpacity={0.7}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
    >
      {isChecked && <Text style={styles.tick}>✓</Text>}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  box: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 1.5,
    alignItems: "center",
    justifyContent: "center",
  },
  tick: {
    color: colors.white,
    fontSize: 13,
    fontWeight: "700",
    lineHeight: 16,
  },
  imageRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  image: {
    width: 22,
    height: 22,
  },
  imageLabel: {
    marginLeft: 8,
    fontSize: 14,
    color: colors.mediumBlack,
  },
});

export default Checkbox;
